import { ChannelUserWorkspace } from '@/types/generated/graphql';
import { AppAvatar } from '@components/app-avatar';
import { useTogglePinAssignChannels } from '@modules/channels/hooks/use-pin-unpin-channel';
import { useSetAsDefault } from '@modules/channels/hooks/use-set-as-default';
import { useScreenSize } from '@shared/hooks/use-screen';
import { copyToClipboard, phoneParser } from '@shared/utils';
import { capitalize } from '@shared/utils/capitalize';
import { Badge, Dropdown, Tooltip } from 'antd';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  RiVerifiedBadgeLine,
  RiFileCopyLine,
  RiVerifiedBadgeFill,
  RiPushpin2Fill,
  RiPushpin2Line,
  RiMore2Fill,
  RiSettingsLine,
} from 'react-icons/ri';
import { createSearchParams, useNavigate } from 'react-router-dom';

export function ChannelCard(data: ChannelUserWorkspace & { disableActions?: boolean }) {
  const { disableActions } = data;
  const navigate = useNavigate();
  const { setAsDefault } = useSetAsDefault();
  const { isMobile } = useScreenSize();
  const { togglePinChannelsHandler } = useTogglePinAssignChannels();
  const { t } = useTranslation();

  return (
    <div className="flex items-center pr-1 group">
      <AppAvatar className="shrink-0 size-7! lg:size-9! border-gray-500!" src={data?.channel?.country?.flag} />
      <section className="grow overflow-hidden max-w-[150px] pl-2.5">
        <div className="flex items-center gap-1">
          <p className="truncate max-w-52 leading-5 text-[15px] lg:text-base text-text-700 group-[.ant-menu-item-selected]:text-text-800">
            {data?.channel?.friendlyName}
          </p>
          <div className="flex gap-1">
            {data?.pinned ? (
              <Tooltip title={capitalize(t('pinned_number'))} rootClassName="mobile-hide-tooltip">
                <span className="inline-flex">
                  <RiPushpin2Fill className="size-4 shrink-0 text-text-600" />
                </span>
              </Tooltip>
            ) : undefined}
            {data?.isDefault ? (
              <Tooltip title={capitalize(t('default_number'))} rootClassName="mobile-hide-tooltip">
                <span className="inline-flex">
                  <RiVerifiedBadgeFill className="shrink-0 text-primary-500 size-4" />
                </span>
              </Tooltip>
            ) : undefined}
          </div>
        </div>
        <p className="text-[13px] lg:text-sm leading-4.5 text-text-600 group-[.ant-menu-item-selected]:text-text-700">
          {phoneParser.format(data?.channel?.phoneNumber)}
        </p>
      </section>

      <div
        className={clsx(
          'flex flex-1 justify-end items-center text-sm group-hover:animate-disappear-bottom-xxs',
          isMobile && 'group-focus:animate-disappear-bottom-xxs',
          !data?.channel?.unreadCounter && 'pr-1'
        )}
      >
        {data?.channel?.unreadCounter ? (
          <Badge count={data?.channel?.unreadCounter} style={{ boxShadow: 'unset' }} />
        ) : undefined}
      </div>

      <span
        className={clsx(
          'hidden absolute right-2 group-hover:block group-hover:animate-appear-bottom-xxs place-items-end',
          isMobile && 'group-focus:block group-focus:animate-appear-bottom-xxs'
        )}
      >
        <Dropdown
          placement="bottomRight"
          rootClassName="prevent-click-outside"
          // Demo: the dropdown never opens, so every item below stays fully
          // unreachable — no navigation, no mutation, no toast.
          open={disableActions ? false : undefined}
          trigger={disableActions ? [] : ['click']}
          menu={{
            items: [
              {
                key: 1,
                icon: <RiFileCopyLine size={20} />,
                label: capitalize(t('copy_number')),
                onClick: (e) => {
                  e?.domEvent?.stopPropagation();
                  copyToClipboard(data?.channel?.phoneNumber + '');
                },
              },
              {
                key: 2,
                icon: <RiPushpin2Line size={20} />,
                label: data?.pinned ? capitalize(t('unpin_number')) : capitalize(t('pin_number')),
                onClick: (e) => {
                  e?.domEvent?.stopPropagation();
                  togglePinChannelsHandler(data?.channel?.id ?? 0);
                },
              },
              {
                key: 3,
                style: { display: data?.isDefault ? 'none' : 'flex' },
                icon: <RiVerifiedBadgeLine size={20} />,
                label: capitalize(t('set_as_default')),
                onClick: (e) => {
                  e?.domEvent?.stopPropagation();
                  setAsDefault(data?.channel?.id ?? 0);
                },
              },
              {
                key: 4,
                icon: <RiSettingsLine size={20} />,
                label: capitalize(t('number_settings')),
                onClick: (e) => {
                  e?.domEvent?.stopPropagation();
                  navigate({
                    pathname: String('/settings/channel/' + data?.channel?.id),
                    search: createSearchParams({
                      name: data?.channel?.friendlyName + '',
                    }).toString(),
                  });
                },
              },
            ],
          }}
        >
          <div
            className="size-7 grid place-items-center bg-white shadow rounded-full shrink-0"
            onClick={(e) => e?.stopPropagation()}
          >
            <RiMore2Fill className="size-4" />
          </div>
        </Dropdown>
      </span>
    </div>
  );
}
